import {findByFieldName} from '../../../../fields'
import {SLOT_MANAGEMENT_DEPENDENT_FIELDS} from '../../../../slotManagement'
import {ChangeableFields} from '../../changeableFields/types'

/**
 * Slot-managed mix fields. A change to any of these is a "mix" change that the backend can validate
 * via the lightweight path (HCP-108663). At least one of them must be dirty for the short-circuit
 * to apply:
 * - `material`: the concrete material / mix the user selected.
 * - `materialVariations`: the slump / setControl overlay (relevant for AU). These can change
 *   WITHOUT a `material` change, so they count as a mix change on their own - mirroring the backend
 *   lightweight set (`Material` / `Slump` / `SetControl`).
 */
const MIX_FIELDS: ReadonlyArray<(typeof SLOT_MANAGEMENT_DEPENDENT_FIELDS)[number]> = [
  'material',
  'materialVariations'
]

/**
 * Slot-managed fields that get dirtied as a side effect of a mix change, and are therefore
 * tolerated when deciding whether "only the mix changed":
 * - the mix fields themselves (`material`, `materialVariations`).
 * - `contract`: a different material can belong to a different contract item, so selecting a
 *   material may switch the contract.
 * - `spacingOrFlowRate`: auto-adjusted (e.g. by useLoadSpacingAdjustment) for the new material.
 */
const MIX_CHANGE_ALLOWED_FIELDS: ReadonlyArray<(typeof SLOT_MANAGEMENT_DEPENDENT_FIELDS)[number]> =
  [...MIX_FIELDS, 'contract', 'spacingOrFlowRate']

/**
 * Determines whether the mix is the ONLY changed slot-managed field.
 *
 * Business rule (HCP-108663):
 * When only the mix changed, the backend can short-circuit the expensive OTV/SynchroTess slot
 * validation and return `valid: true` if the resolved plant / dispatch group did not change. This
 * helper drives the `checkOnlyIfLogisticsChanged` flag sent to POST /rmcSlots/validate.
 *
 * "Mix only" means at least one mix field (`material` or `materialVariations` - the slump / setControl
 * overlay) is dirty, and no slot-managed field other than the mix fields and their known side effects
 * (`contract`, `spacingOrFlowRate`) is dirty. This mirrors the backend lightweight set
 * (`Material` / `Slump` / `SetControl`). Being permissive here is safe because the backend
 * independently re-checks whether the plant / dispatch group actually changed before short-circuiting.
 *
 * @param params.changeableFields - Current changeable field states (from orderChangeRulesOnly mode)
 */
export const isOnlyMixChanged = ({
  changeableFields
}: {
  changeableFields: ChangeableFields
}): boolean => {
  // At least one mix field must be the changed field
  const isMixDirty = MIX_FIELDS.some(
    (fieldName) => findByFieldName(changeableFields, fieldName)?.isDirty
  )
  if (!isMixDirty) return false

  // No slot-managed field other than the mix fields and their known side effects may be dirty
  const isOtherSlotFieldDirty = SLOT_MANAGEMENT_DEPENDENT_FIELDS.filter(
    (fieldName) => !MIX_CHANGE_ALLOWED_FIELDS.includes(fieldName)
  ).some((fieldName) => findByFieldName(changeableFields, fieldName)?.isDirty)

  return !isOtherSlotFieldDirty
}
